import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { useConfirm, useConfirmUnsaved } from 'hooks/useConfirm';
import { useEsc } from 'hooks/useEsc';
import { useForceUpdate } from 'hooks/useForceUpdate';
import {
  ScreenSize,
  UI_DATE_FORMAT_LONG,
  UI_DATE_TIME_FORMAT_LONG,
} from 'lib/constants';
import { dateToIsoString, formatDate, isoDateStringToDate } from 'lib/riskbase';
import { smartText } from 'lib/str';
import { theme } from 'lib/theme';
import { cloneDeep, isEqual } from 'lodash';
import React from 'react';
import { View } from 'react-native';
import { useDispatch } from 'react-redux';
import {
  ALERT,
  doEngageContactEvacuationInfoLoad,
  doEngageSaveContact,
  eventTypeEngageContactAdded,
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
} from 'store/actions';
import { TextField } from 'components/Field/TextField';
import { TextAreaField } from 'components/Field/TextAreaField/TextAreaField';
import {
  ContactAddedVia,
  DataSource,
  EngageFlatContact,
  FlatContactType,
} from 'types/Engage';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { SeparatorHorizontal } from 'components/SeparatorHorizontal';
import { selectEngageFlat } from 'store/remote/engageSelectors';
import { useSelector } from 'hooks/useSelector';
import { ButtonToggleGroup } from 'components/ButtonToggleGroup';
import { ICONS } from 'components/Icon';
import { EngageFlatModalCommunicationsPage } from './EngageFlatModalCommunicationsPage';
import { createId } from '@paralleldrive/cuid2';
import { isEmail, isPhoneNumber } from 'class-validator';
import {
  EngageContactInteractionLog,
  EngageContactInteractionType,
} from 'types/DTOs/BlockManagerDTOs';
import { doAddEvent, doRemoveEvent } from 'lib/events';
import { useScreenSize } from 'hooks/useScreenSize';

type HandleSetContactType = (
  arg:
    | {
        isOwner: boolean;
        isResident: boolean;
        type?: never;
      }
    | { isOwner?: never; isResident?: never; type: FlatContactType },
) => void;

type EngageContactModalContact = Omit<
  EngageFlatContact,
  'interactionLogs' | 'hasSupportNeeds'
>;

type HandleChangeWithName = <N extends keyof EngageContactModalContact>(
  name: N,
  value: EngageContactModalContact[N],
  doForceUpdate?: boolean,
) => void;

const formatEvacuationInteractionCount = (
  count: number,
  interactionType: 'viewed' | 'edited',
) =>
  count === 1
    ? `${interactionType} once`
    : count === 2
      ? `${interactionType} twice`
      : `${interactionType} ${count} times`;

function normalizeContact(
  contact: EngageContactModalContact,
): EngageContactModalContact {
  const normalizedContact = { ...contact };
  if (normalizedContact.isOwner || !normalizedContact.isResident) {
    normalizedContact.isUnder16 = false;
  }
  if (normalizedContact.isUnder16) {
    normalizedContact.firstName = '';
    normalizedContact.lastName = '';
    normalizedContact.phone = '';
    normalizedContact.email = '';
  }

  // @todo actually set this when !isOwner && !isResident
  normalizedContact.contactType =
    normalizedContact.isOwner && normalizedContact.isResident
      ? FlatContactType.residentOwner
      : normalizedContact.isOwner
        ? FlatContactType.owner
        : normalizedContact.isResident
          ? FlatContactType.resident
          : normalizedContact.contactType;

  return normalizedContact;
}

export const EngageContactModal: React.FC<
  ModalScreenProps<ModalScreens.EngageContact>
> = ({ route, navigation }) => {
  const dispatch = useDispatch();
  const confirm = useConfirm();
  const confirmUnsaved = useConfirmUnsaved();
  const forceUpdate = useForceUpdate();

  const { key, contactId: incomingContactId } = route.params;

  const [contactId, setContactId] = React.useState<string | null>(
    incomingContactId,
  );
  const [activeTab, setActiveTab] = React.useState<'information' | 'messages'>(
    'information',
  );

  React.useEffect(() => {
    const event = doAddEvent(eventTypeEngageContactAdded, (action) => {
      setContactId(action.payload.contactId);
    });
    return () => {
      doRemoveEvent(event);
    };
  }, []);

  const {
    flat: { name: flatName, id: flatId },
    property: { id: propertyId },
    contacts,
  } = useSelector((state) => selectEngageFlat(state, key))!;

  const contact = contactId
    ? contacts.find((c) => c.id === contactId)
    : undefined;

  const initValues = React.useMemo<EngageContactModalContact>(
    () =>
      contact
        ? {
            id: contact.id,
            firstName: contact.firstName,
            lastName: contact.lastName,
            email: contact.email,
            phone: contact.phone,
            isOwner: contact.isOwner,
            isResident: contact.isResident,
            isUnder16: contact.isUnder16,
            isPrimary: contact.isPrimary,
            internalNotes: contact.internalNotes,
            comments: contact.comments,
            contactType: contact.contactType,
            confirmedDate: contact.confirmedDate,
            confirmationExpiryDate: contact.confirmationExpiryDate ?? null,
            addedTimestamp: contact.addedTimestamp,
            addedVia: contact.addedVia,
            archivedTimestamp: contact.archivedTimestamp,
            archivedVia: contact.archivedVia,
            archivedReason: contact.archivedReason,
          }
        : {
            id: createId(),
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            isOwner: true,
            isResident: false,
            isUnder16: false,
            isPrimary: false,
            internalNotes: '',
            comments: '',
            contactType: FlatContactType.owner,
            confirmedDate: null,
            confirmationExpiryDate: null,
            addedTimestamp: dateToIsoString(new Date()),
            addedVia: DataSource.app,
            archivedTimestamp: null,
            archivedVia: null,
            archivedReason: null,
          },
    [contact],
  );

  const changes = React.useRef(cloneDeep(initValues));
  // If ID is different the contact has changed, reset changes
  if (initValues.id !== changes.current.id) {
    changes.current = cloneDeep(initValues);
  }
  const checkIsDirty = React.useCallback(
    () => !isEqual(initValues, changes.current),
    [initValues],
  );
  const isDirty = React.useRef<boolean>(false);
  isDirty.current = checkIsDirty();

  const isNew = !contactId;
  const isArchived = !!changes.current.archivedTimestamp;

  const handleSetContactType: HandleSetContactType = ({
    isOwner,
    isResident,
    type,
  }) => {
    if (type) {
      changes.current.isOwner = false;
      changes.current.isResident = false;
      changes.current.contactType = type;
    } else {
      changes.current.isOwner = isOwner;
      changes.current.isResident = isResident;
      changes.current.contactType =
        isOwner && isResident
          ? FlatContactType.residentOwner
          : isOwner
            ? FlatContactType.owner
            : FlatContactType.resident;
    }
    forceUpdate();
  };

  const handleChangeWithName: HandleChangeWithName = React.useCallback(
    (name, value, doForceUpdate) => {
      changes.current[name] = value;
      if (doForceUpdate || isDirty.current !== checkIsDirty()) {
        forceUpdate();
      }
    },
    [checkIsDirty, forceUpdate],
  );

  const handleSave = React.useCallback(
    (options?: { actionAfterSave?: 'openEmergencyEvacuationDetails' }) => {
      const normalizedContact = normalizeContact(changes.current);
      const errors = [];

      if (
        !normalizedContact.isUnder16 &&
        (normalizedContact.firstName.trim() === '' ||
          normalizedContact.lastName.trim() === '')
      ) {
        errors.push('*First Name* and *Last Name* are required');
      }

      if (
        normalizedContact.phone &&
        !(
          // Allow GB numbers without international code
          // Allow international number with international code
          (
            isPhoneNumber(normalizedContact.phone, 'GB') ||
            isPhoneNumber(normalizedContact.phone)
          )
        )
      ) {
        errors.push('*Phone* is not valid');
      }

      const trimmedEmail = (normalizedContact.email || '').trim();
      normalizedContact.email = trimmedEmail;
      if (normalizedContact.isPrimary) {
        if (!trimmedEmail || !isEmail(trimmedEmail)) {
          errors.push('A primary contact requires an *email* address');
        }
      } else if (trimmedEmail && !isEmail(trimmedEmail)) {
        errors.push('*Email* is not valid');
      }

      if (errors.length) {
        dispatch(
          ALERT({
            title: 'Contact Cannot Be Saved',
            text: errors.join('\n'),
          }),
        );
        return;
      }

      dispatch(
        doEngageSaveContact({
          key,
          propertyId,
          flatId,
          isNew,
          contact: normalizedContact,
          actionAfterSave: options?.actionAfterSave,
        }),
      );
    },
    [dispatch, flatId, isNew, key, propertyId],
  );

  const handleClose = React.useCallback(() => {
    if (isDirty.current) {
      confirmUnsaved({
        onSave: () => handleSave,
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  }, [confirmUnsaved, handleSave, dispatch]);

  const isDirtyVar = isDirty.current;
  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirtyVar });
  }, [navigation, isDirtyVar]);
  useEsc(handleClose);

  const NavigationHeaderMemo = React.useCallback(
    (allowSave: boolean) => (
      <NavigationHeader
        heading={
          isNew ? 'New Contact' : isArchived ? 'Archived Contact' : 'Contact'
        }
        subheading={flatName}
        cancelButtonText="Close"
        onPressCancelButton={handleClose}
        onPressSaveButton={
          isArchived ? undefined : allowSave ? handleSave : null
        }
        menuRight={
          !isNew && {
            iconButtonConfig: {
              icon: ICONS.EllipsisLight,
            },
            options: [
              {
                text: 'Send Message',
                onPress: () =>
                  dispatch(
                    NAVIGATE_MODAL({
                      screen: ModalScreens.EngageSendCommunication,
                      params: {
                        key,
                        propertyId,
                        flatIds: [flatId],
                        contactId: changes.current.id,
                        mode: 'singleFlat',
                      },
                    }),
                  ),
              },
            ],
          }
        }
      />
    ),
    [
      dispatch,
      flatId,
      flatName,
      handleClose,
      handleSave,
      isArchived,
      isNew,
      key,
      propertyId,
    ],
  );

  const ButtonToggleGroupMemo = React.useCallback(
    (thisActiveTab: 'information' | 'messages') => (
      <ButtonToggleGroup
        key={thisActiveTab}
        rounded
        buttons={[
          { text: 'Information', key: 'information' },
          { text: 'Messages', key: 'messages' },
        ]}
        active={thisActiveTab}
        onActiveChange={(newActive) => {
          setTimeout(() => setActiveTab(newActive), 1);
          return false;
        }}
        containerStyle={{
          marginBottom: theme.paddingVerticalHalf,
          alignSelf: 'stretch',
          marginHorizontal: theme.paddingHorizontal,
        }}
        size="small"
      />
    ),
    [],
  );

  const handleOpenEvacuationInfo = React.useCallback(() => {
    if (isNew) {
      confirm({
        title: 'Create Contact',
        text: 'This contact hasn’t been created yet. Do you want to create them and open their emergency evacuation details?',
        confirmText: 'Create and Open',
        onConfirm: () => {
          handleSave({
            actionAfterSave: 'openEmergencyEvacuationDetails',
          });
        },
      });
    } else {
      dispatch(
        doEngageContactEvacuationInfoLoad({
          key,
          propertyId,
          flatId,
          contactId: changes.current.id,
          contactName:
            `${changes.current.firstName} ${changes.current.lastName}`.trim() ||
            'Contact',
        }),
      );
    }
  }, [confirm, dispatch, flatId, handleSave, isNew, key, propertyId]);

  return (
    <>
      <View
        style={[
          activeTab === 'information' ? { flex: 1 } : { display: 'none' },
        ]}
      >
        <EngageContactInfoPage
          HeaderItems={
            <>
              {NavigationHeaderMemo(true)}
              {!isNew && ButtonToggleGroupMemo('information')}
            </>
          }
          handleSetContactType={handleSetContactType}
          handleChangeWithName={handleChangeWithName}
          handleOpenEvacuationInfo={handleOpenEvacuationInfo}
          changes={changes}
          isNew={isNew}
          evacuationInfoLogs={contact?.interactionLogs || []}
        />
      </View>
      {!isNew && (
        <View
          style={[activeTab === 'messages' ? { flex: 1 } : { display: 'none' }]}
        >
          <EngageFlatModalCommunicationsPage
            pageKey={key}
            contactId={changes.current.id}
            HeaderItems={
              <>
                {NavigationHeaderMemo(false)}
                {ButtonToggleGroupMemo('messages')}
              </>
            }
          />
        </View>
      )}
    </>
  );
};

const EngageContactInfoPage: React.FC<{
  HeaderItems: React.ReactNode;
  handleSetContactType: HandleSetContactType;
  handleChangeWithName: HandleChangeWithName;
  handleOpenEvacuationInfo: () => void;
  changes: React.RefObject<EngageContactModalContact>;
  isNew: boolean;
  evacuationInfoLogs: EngageContactInteractionLog[];
}> = ({
  HeaderItems,
  handleSetContactType,
  handleChangeWithName,
  handleOpenEvacuationInfo,
  changes,
  isNew,
  evacuationInfoLogs,
}) => {
  const dispatch = useDispatch();
  const [showEvacuationInfoLogs, setShowEvacuationInfoLogs] =
    React.useState(false);
  const screenSize = useScreenSize();

  const isArchived = !!changes.current.archivedTimestamp;
  const evacuationInfoViewLogCount = evacuationInfoLogs.filter(
    (log) =>
      log.interactionType ===
      EngageContactInteractionType.flatContactViewEvacuationInfo,
  ).length;
  const evacuationInfoEditLogCount = evacuationInfoLogs.filter(
    (log) =>
      log.interactionType ===
      EngageContactInteractionType.flatContactEditEvacuationInfo,
  ).length;
  const evacuationInfoSummaryText =
    evacuationInfoLogs.length === 0
      ? 'Information has not been viewed or edited'
      : evacuationInfoViewLogCount > 0 && evacuationInfoEditLogCount > 0
        ? `Information ${formatEvacuationInteractionCount(
            evacuationInfoViewLogCount,
            'viewed',
          )} and ${formatEvacuationInteractionCount(
            evacuationInfoEditLogCount,
            'edited',
          )}`
        : evacuationInfoViewLogCount > 0
          ? `Information ${formatEvacuationInteractionCount(
              evacuationInfoViewLogCount,
              'viewed',
            )}`
          : `Information ${formatEvacuationInteractionCount(
              evacuationInfoEditLogCount,
              'edited',
            )}`;

  const collectDetails =
    changes.current.isOwner ||
    (changes.current.isResident && !changes.current.isUnder16) ||
    (!changes.current.isOwner && !changes.current.isResident);

  return (
    <PageScrollView isInModal={true} header={HeaderItems}>
      <Card label="Relationship to Flat">
        <ViewWithSeparators horizontal>
          <CardRow
            label="Owner"
            radioValue={changes.current.isOwner && !changes.current.isResident}
            onPress={() =>
              handleSetContactType({ isOwner: true, isResident: false })
            }
            style={{ flex: 1 }}
          />
          <CardRow
            label="Owner & Resident"
            radioValue={changes.current.isOwner && changes.current.isResident}
            onPress={() =>
              handleSetContactType({
                isOwner: true,
                isResident: true,
              })
            }
            style={{ flex: 1 }}
          />
        </ViewWithSeparators>
        <ViewWithSeparators horizontal>
          <CardRow
            label="Resident"
            radioValue={!changes.current.isOwner && changes.current.isResident}
            onPress={() =>
              handleSetContactType({ isOwner: false, isResident: true })
            }
            style={{ flex: 1 }}
          />
          <CardRow
            label="Letting Agent"
            style={{ flex: 1 }}
            radioValue={
              changes.current.contactType === FlatContactType.lettingAgent
            }
            onPress={() =>
              handleSetContactType({ type: FlatContactType.lettingAgent })
            }
            disabled
          />
        </ViewWithSeparators>
        <ViewWithSeparators horizontal>
          <CardRow
            label="Behalf of Someone Else"
            style={{ flex: 1 }}
            radioValue={
              changes.current.contactType === FlatContactType.onBehalfOf
            }
            onPress={() =>
              handleSetContactType({ type: FlatContactType.onBehalfOf })
            }
            disabled
          />
          <CardRow
            label="Other"
            style={{ flex: 1 }}
            radioValue={changes.current.contactType === FlatContactType.other}
            onPress={() =>
              handleSetContactType({ type: FlatContactType.other })
            }
            disabled
          />
        </ViewWithSeparators>
      </Card>

      {(changes.current.contactType === FlatContactType.onBehalfOf ||
        changes.current.contactType === FlatContactType.other ||
        changes.current.contactType === FlatContactType.lettingAgent) && (
        <Card
          label="Other Description"
          explanation={smartText(
            changes.current.contactType === FlatContactType.onBehalfOf
              ? 'Describe how this person knows the owners / residents – for example, they are a carer or a family member'
              : changes.current.contactType === FlatContactType.lettingAgent
                ? 'Provide details about the letting agent'
                : "Describe this person's relationship to the flat",
          )}
        >
          <TextAreaField
            onChangeWithName={handleChangeWithName}
            name="comments"
            value={changes.current.comments}
          />
        </Card>
      )}

      <ViewWithSeparators
        horizontal={screenSize !== ScreenSize.small}
        separatorColor="transparent"
        separatorMargin={theme.paddingHorizontalHalf}
        marginHorizontal
      >
        {changes.current.isResident && !changes.current.isOwner && (
          <Card
            label="Resident Age"
            labelLinkText="Why is this important?"
            onLabelLinkPress={() => {
              dispatch(
                ALERT({
                  title: 'Resident Age',
                  text: smartText(
                    'The Building Safety Act and Fire Safety (England) Regulations place additional responsibilities on the Responsible and Accountable People relating to residents aged 16 and over.\n\n' +
                      "For data protection reasons, we don't collect names or contact details for people under 16. We only ask whether they might need help evacuating.\n\n" +
                      'If a resident under 16 is also an owner, their details are collected. This is because owners have additional legal responsibilities under the Building Safety Act.',
                  ),
                }),
              );
            }}
            explanation={smartText(
              'Contact details are only collected for residents 16 years or over',
            )}
            marginHorizontal={false}
            marginBottomQuarter={
              screenSize === ScreenSize.small && collectDetails
            }
            style={{ flex: 1 }}
          >
            <CardRow
              value="16 Years or Over"
              onPress={
                isArchived
                  ? undefined
                  : () =>
                      handleChangeWithName(
                        'isUnder16',
                        !changes.current.isUnder16,
                        true,
                      )
              }
              checkedValue={!changes.current.isUnder16}
              disabled={changes.current.isOwner}
            />
          </Card>
        )}

        {collectDetails && (
          <Card
            style={{ flex: 1 }}
            label="Primary Contact"
            explanation={smartText(
              'Most messages will go to primary contacts.',
            )}
            marginHorizontal={false}
          >
            <CardRow
              label="Is Primary Contact"
              labelAlignTop
              onPress={
                isArchived
                  ? undefined
                  : () =>
                      handleChangeWithName(
                        'isPrimary',
                        !changes.current.isPrimary,
                        true,
                      )
              }
              checkedValue={changes.current.isPrimary}
            />
          </Card>
        )}
      </ViewWithSeparators>

      {collectDetails && (
        <ViewWithSeparators
          horizontal
          separatorColor="transparent"
          separatorMargin={theme.paddingHorizontalHalf}
          marginHorizontal
        >
          <Card
            style={{ flex: 1 }}
            label="First Name"
            required
            marginHorizontal={false}
            marginBottomHalf
          >
            {isArchived ? (
              <CardRow value={changes.current.firstName} />
            ) : (
              <TextField
                value={changes.current.firstName}
                containerStyle={{ flex: 1 }}
                name="firstName"
                onChangeWithName={handleChangeWithName}
                disabled={!collectDetails}
              />
            )}
          </Card>
          <Card
            style={{ flex: 1 }}
            label="Last Name"
            required
            marginHorizontal={false}
            marginBottomHalf
          >
            {isArchived ? (
              <CardRow value={changes.current.lastName} />
            ) : (
              <TextField
                value={changes.current.lastName}
                containerStyle={{ flex: 1 }}
                name="lastName"
                onChangeWithName={handleChangeWithName}
                disabled={!collectDetails}
              />
            )}
          </Card>
        </ViewWithSeparators>
      )}

      {collectDetails && (
        <ViewWithSeparators
          horizontal
          separatorColor="transparent"
          separatorMargin={theme.paddingHorizontalHalf}
          marginHorizontal
        >
          <Card
            label="Email"
            style={{ flex: 1 }}
            marginHorizontal={false}
            required={changes.current.isPrimary}
          >
            {isArchived ? (
              <CardRow value={changes.current.email} placeholder="No Email" />
            ) : (
              <TextField
                value={changes.current.email}
                containerStyle={{ flex: 1 }}
                placeholder="No Email"
                name="email"
                onChangeWithName={handleChangeWithName}
                disabled={!collectDetails}
              />
            )}
          </Card>
          <Card label="Phone" style={{ flex: 1 }} marginHorizontal={false}>
            {isArchived ? (
              <CardRow value={changes.current.phone} placeholder="No Phone" />
            ) : (
              <TextField
                value={changes.current.phone}
                containerStyle={{ flex: 1 }}
                placeholder="No Phone Number"
                name="phone"
                onChangeWithName={handleChangeWithName}
                disabled={!collectDetails}
              />
            )}
          </Card>
        </ViewWithSeparators>
      )}

      {changes.current.isResident && (
        <Card label="Emergency Evacuation" marginBottomQuarter>
          <CardRow
            label="Manage contact's personal details"
            value="These details are sensitive, viewing and editing is logged."
            labelAlignTop
            icon={ICONS.EyeSlashLight}
            onPress={handleOpenEvacuationInfo}
            disabled={isArchived}
          />
          <CardRow
            label={evacuationInfoSummaryText}
            icon={
              evacuationInfoLogs.length > 0 ? ICONS.ChevronLeftLight : undefined
            }
            iconStyle={[
              showEvacuationInfoLogs
                ? {
                    transform: [{ rotate: '270deg' }],
                  }
                : {
                    transform: [{ rotate: '180deg' }],
                  },
            ]}
            onPress={
              evacuationInfoLogs.length > 0
                ? () => setShowEvacuationInfoLogs((current) => !current)
                : undefined
            }
          />
          {showEvacuationInfoLogs &&
            evacuationInfoLogs.map((log, index) => {
              const logDate = isoDateStringToDate(log.createdDate);
              const interactionTypeText =
                log.interactionType ===
                EngageContactInteractionType.flatContactEditEvacuationInfo
                  ? 'Edited'
                  : 'Viewed';
              return (
                <CardRow
                  key={index}
                  label={log.name}
                  label2={`${log.email} - ${interactionTypeText}`}
                  value={formatDate(logDate, UI_DATE_TIME_FORMAT_LONG)}
                />
              );
            })}
        </Card>
      )}

      {changes.current.isResident && (
        <View
          style={{
            height: theme.paddingVerticalHalf + theme.paddingVerticalQuarter,
          }}
        />
      )}

      <Card
        label="Notes"
        explanation="Notes are for internal use only and are not visible to contacts"
      >
        {isArchived ? (
          <CardRow
            placeholder="No Internal Notes"
            value={changes.current.internalNotes}
          />
        ) : (
          <TextAreaField
            placeholder="Internal Notes"
            name="internalNotes"
            value={changes.current.internalNotes}
            onChangeWithName={handleChangeWithName}
          />
        )}
      </Card>

      <SeparatorHorizontal withMarginBottom />

      <Card
        explanation={
          !isNew
            ? 'Added ' +
              `${formatDate(isoDateStringToDate(changes.current.addedTimestamp), UI_DATE_FORMAT_LONG)} ` +
              `via ${ContactAddedVia[changes.current.addedVia].name}${
                changes.current.archivedTimestamp && changes.current.archivedVia
                  ? '\nArchived ' +
                    `${formatDate(isoDateStringToDate(changes.current.archivedTimestamp), UI_DATE_FORMAT_LONG)} ` +
                    `via ${ContactAddedVia[changes.current.archivedVia].name}`
                  : ''
              }`
            : ''
        }
      />
    </PageScrollView>
  );
};
